/** The API Requests search namespaces wired to their suggestion fetchers.
 *
 * demo_api_request is a very large, fast-growing, UNPARTITIONED log table and
 * a `created` time window is MANDATORY in every search (the page gates the
 * query client-side; the backend 400s). The identity namespaces (id, uuid,
 * request, booking, tenant, errors, error_series, created) are exact,
 * index-backed matches; integration/type/duration/store are unindexed filters
 * applied WITHIN the mandatory window. Exact-only namespaces round-trip fine
 * through the `=` sigil (`=` resolves to `exact` when `iexact` is absent);
 * `duration` uses the open-ended range syntax for gt/lt (`5..`, `..5`).
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (contract-tested).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/apiRequests';

import { API_REQUEST_STORE_LABELS, API_REQUEST_TYPE_LABELS } from './apiRequestFormat';

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildApiRequestNamespaces(): NamespaceConfigMap {
  return {
    booking: {
      label: 'booking',
      operators: ['exact'],
      valueType: 'string',
      help: "External booking ID the webhook carried, or a booking's alt id (exact).",
      examples: ['booking=BOKU-123456'],
    },
    request: {
      label: 'request',
      operators: ['exact'],
      valueType: 'string',
      help: 'HTTP request ID assigned by the platform (exact).',
      examples: ['request=abcd1234efgh'],
    },
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id.',
      examples: ['tenant=curatedplanet', 'tenant=42'],
    },
    errors: {
      label: 'errors',
      operators: ['exact'],
      valueType: 'string',
      fetchSuggestions: localLabels(['yes', 'no']),
      help: 'Whether the request has an error series — set when a webhook (re)import failed, cleared by a successful reimport.',
      examples: ['errors=yes'],
    },
    error_series: {
      label: 'error_series',
      operators: ['exact'],
      valueType: 'string',
      help: 'Error series ID shared by requests that failed together (exact).',
      examples: ['error_series=3fA9kX2b'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the request was received, UTC. `=` matches a whole day; a range takes date-time bounds. MANDATORY — every search must carry a created window (the table is too large to scan unbounded).',
      examples: ['created=2026-07-20', 'created="2026-07-14 09:00".."2026-07-21 18:00"'],
    },
    integration: {
      label: 'integration',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Integration codename the webhook belongs to (equals, case-insensitive). Unindexed — applied within the created window.',
      examples: ['integration=bokun', 'integration=ventrata'],
    },
    type: {
      label: 'type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(API_REQUEST_TYPE_LABELS),
      help: 'Request type, by label or raw integer value. Unindexed — applied within the created window.',
      examples: ['type="booking webhook"', 'type=5'],
    },
    duration: {
      label: 'duration',
      operators: ['exact', 'gt', 'lt', 'range'],
      valueType: 'string',
      help: 'Request duration in SECONDS. Open-ended ranges give greater/less than: `5..` (slower than 5 s), `..0.5` (faster than 0.5 s), `1..10` (between). Unindexed — applied within the created window.',
      examples: ['duration=5..', 'duration=..0.5', 'duration=1..10'],
    },
    status: {
      label: 'status',
      operators: ['exact', 'gt', 'lt', 'range'],
      valueType: 'string',
      help: 'Response HTTP status code. Open-ended ranges give greater/less than: `500..` (≥500), `..400` (<400), `200..299` (2xx). Unindexed — applied within the created window.',
      examples: ['status=500', 'status=400..', 'status=200..299'],
    },
    store: {
      label: 'store',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(API_REQUEST_STORE_LABELS),
      help: 'Store policy, by label or raw integer value. Unindexed — applied within the created window.',
      examples: ['store="store forever"', 'store=0'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'string',
      help: 'API request integer id (exact).',
      examples: ['id=123456789'],
    },
    uuid: {
      label: 'uuid',
      operators: ['exact'],
      valueType: 'string',
      help: 'API request UUID (exact).',
      examples: ['uuid=0d9f1c2e-6f3a-4b8e-9c1d-2a3b4c5d6e7f'],
    },
  };
}
